<!-- 目的地详情 -->
<template>
  <div class="frame destination-detail">
    <div class="box1">
      <div class="hero">
        <img :src="heroImg" class="hero-img" :alt="detail.heroTitle" />
        <div class="hero-overlay">
          <span class="hero-tag" v-if="detail.heroTag">{{ detail.heroTag }}</span>
          <h1 class="hero-title">{{ detail.heroTitle }}</h1>
        </div>
      </div>
      <div class="detail-inner">
        <div class="detail-main">
          <h2 class="tour-title">{{ detail.tourTitle }}</h2>
          <p class="tour-route" v-if="detail.tourRoute">
            {{ detail.tourRoute }}
          </p>
          <p class="tour-desc" v-if="detail.tourDesc">{{ detail.tourDesc }}</p>
          <ul
            v-if="detail.highlights && detail.highlights.length"
            class="highlights-list"
          >
            <li
              v-for="(item, index) in detail.highlights"
              :key="index"
              class="highlights-item"
            >
              <i class="el-icon-check highlights-icon"></i>
              <span>{{ item }}</span>
            </li>
          </ul>
        </div>
        <aside class="booking-box">
          <h3 class="booking-title">{{ $t("destinationDetail.bookTour") }}</h3>
          <p class="booking-contact">
            {{ $t("destinationDetail.bookContact") }}
          </p>
          <dl class="booking-meta">
            <div class="meta-row">
              <dt>{{ $t("destinationDetail.labels.tourCode") }}</dt>
              <dd>{{ detail.tourCode }}</dd>
            </div>
            <div class="meta-row">
              <dt>{{ $t("destinationDetail.labels.time") }}</dt>
              <dd>{{ detail.tourDays }}</dd>
            </div>
            <div class="meta-row">
              <dt>{{ $t("destinationDetail.labels.cities") }}</dt>
              <dd>{{ detail.tourCities }}</dd>
            </div>
            <div class="meta-row">
              <dt>{{ $t("destinationDetail.labels.accommodation") }}</dt>
              <dd>{{ detail.tourAccommodation }}</dd>
            </div>
          </dl>
          <button type="button" class="buy-btn" @click="openContactForm">
            {{ $t("destinationDetail.buyNow") }}
          </button>
        </aside>
      </div>
    </div>

    <div class="box3">
      <div class="section-inner">
        <div
          v-for="(day, dayIndex) in detail.days"
          :key="dayIndex"
          class="itinerary-day"
        >
          <h3 class="day-title">
            <i class="el-icon-s-promotion day-icon"></i>
            {{ day.title }}
          </h3>
          <ul class="day-meta" v-if="day.hotel || day.route || day.meals">
            <li v-if="day.route">{{ day.route }}</li>
            <li v-if="day.hotel">
              {{ $t("destinationDetail.labels.hotel") }}：{{ day.hotel }}
            </li>
            <li v-if="day.meals">
              {{ $t("destinationDetail.labels.meals") }}：{{ day.meals }}
            </li>
          </ul>
          <p
            v-for="(para, pIdx) in day.paragraphs"
            :key="pIdx"
            class="day-para"
          >
            {{ para }}
          </p>
          <div
            v-if="getDayImages(dayIndex).length"
            class="day-gallery"
            :class="{
              'day-gallery--double': getDayImages(dayIndex).length === 2,
            }"
          >
            <img
              v-for="(img, gIdx) in getDayImages(dayIndex)"
              :key="gIdx"
              :src="img"
              :alt="day.title"
            />
          </div>
          <button
            type="button"
            class="buy-btn buy-btn--inline"
            @click="openContactForm"
          >
            {{ $t("destinationDetail.buyNow") }}
          </button>
        </div>
      </div>
    </div>

    <div
      class="box-fee"
      v-if="
        detail.feeIncluded ||
        detail.feeExcluded ||
        (detail.feeRemarks && detail.feeRemarks.length)
      "
    >
      <div class="section-inner">
        <h2 class="section-title">{{ $t("destinationDetail.feeTitle") }}</h2>
        <div class="fee-block" v-if="detail.feeIncluded">
          <h3 class="fee-label">
            {{ $t("destinationDetail.feeIncludedLabel") }}
          </h3>
          <p class="fee-text">{{ detail.feeIncluded }}</p>
        </div>
        <div class="fee-block" v-if="detail.feeExcluded">
          <h3 class="fee-label">
            {{ $t("destinationDetail.feeExcludedLabel") }}
          </h3>
          <p class="fee-text">{{ detail.feeExcluded }}</p>
        </div>
        <div
          class="fee-block"
          v-if="detail.feeRemarks && detail.feeRemarks.length"
        >
          <h3 class="fee-label">
            {{ $t("destinationDetail.feeRemarksLabel") }}
          </h3>
          <p
            v-for="(note, noteIdx) in detail.feeRemarks"
            :key="noteIdx"
            class="fee-text"
          >
            {{ note }}
          </p>
        </div>
      </div>
    </div>

    <div class="box4">
      <div class="section-inner">
        <h2 class="section-title section-title--center">
          {{ $t("destinationDetail.photosTitle") }}
        </h2>
        <div class="photo-tabs">
          <button
            v-for="tab in photoTabList"
            :key="tab.key"
            type="button"
            class="photo-tab"
            :class="{ active: activePhotoTab === tab.key }"
            @click="activePhotoTab = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>
        <div class="photo-grid">
          <div
            v-for="(photo, index) in filteredPhotos"
            :key="index"
            class="photo-cell"
          >
            <img :src="photo.src" alt="" />
          </div>
        </div>
      </div>
    </div>

    <div class="box5">
      <div class="section-inner">
        <div class="review-section-head">
          <div class="review-section-label">{{ $t("home.reviews.en") }}</div>
          <h2 class="review-section-title">{{ $t("home.reviews.title") }}</h2>
        </div>
        <p class="review-intro">{{ $t("home.reviews.intro") }}</p>
        <div class="review-columns">
          <div
            class="review-column"
            v-for="platform in reviewPlatforms"
            :key="'col-' + platform.id"
          >
            <div class="review-scroll">
              <div
                class="review-block"
                v-for="(review, rIdx) in platform.reviews"
                :key="rIdx"
              >
                <div class="review-meta">
                  <span class="review-author">{{ review.author }}</span>
                  <span class="review-date">{{ review.date }}</span>
                </div>
                <div
                  class="review-stars"
                  :class="'stars--' + platform.starTheme"
                >
                  <i
                    v-for="n in review.rating"
                    :key="n"
                    class="el-icon-star-on"
                  ></i>
                </div>
                <h4 class="review-block-title" v-if="review.title">
                  {{ review.title }}
                </h4>
                <p class="review-content">{{ review.content }}</p>
                <div class="review-images" v-if="review.image">
                  <img :src="review.image" alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ContactFormModal :visible.sync="contactFormVisible" />
  </div>
</template>

<script>
import ContactFormModal from "@/components/ContactFormModal.vue";
import {
  DEST_IMGS,
  SICHUAN_DAY_IMGS,
  SICHUAN_HERO_IMG,
  SCANDCQ_HERO_IMG,
  SCANDCQ_DAY_IMGS,
  BEIJING_HERO_IMG,
  BEIJING_DAY_IMGS,
  XIAN_HERO_IMG,
  XIAN_DAY_IMGS,
} from "@/utils/destinationAssets";
import { enrichReviewPlatforms } from "@/utils/reviewAssets";
import { getOurPhotosByTab } from "@/utils/ourPhotos";

export default {
  name: "DestinationDetail",
  components: { ContactFormModal },
  data() {
    return {
      contactFormVisible: false,
      activePhotoTab: "all",
      destId: 0,
    };
  },
  computed: {
    detail() {
      const list = this.$t("destinationDetail.list");
      const item = list[this.destId];
      if (item) return item;
      return list[0] || {};
    },
    heroImg() {
      if (this.destId === 0) return SICHUAN_HERO_IMG;
      if (this.destId === 1) return SCANDCQ_HERO_IMG;
      if (this.destId === 2) return BEIJING_HERO_IMG;
      if (this.destId === 3) return XIAN_HERO_IMG;
      return DEST_IMGS[this.destId] || DEST_IMGS[0];
    },
    photoTabList() {
      const tabs = this.$t("destinationDetail.photoTabs");
      return Object.keys(tabs).map((key) => ({
        key,
        label: tabs[key],
      }));
    },
    filteredPhotos() {
      return getOurPhotosByTab(this.activePhotoTab || "all");
    },
    reviewPlatforms() {
      return enrichReviewPlatforms(this.$t("home.reviews.platforms"));
    },
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler(id) {
        const parsed = parseInt(id, 10);
        const max = (this.$t("destinationDetail.list") || []).length - 1;
        this.destId =
          Number.isNaN(parsed) || parsed < 0 || parsed > max ? 0 : parsed;
        this.activePhotoTab = "all";
        this.$nextTick(() => {
          window.scrollTo(0, 0);
        });
      },
    },
    "$i18n.locale"() {
      this.$nextTick(() => {
        const max = (this.$t("destinationDetail.list") || []).length - 1;
        if (this.destId > max) this.destId = 0;
      });
    },
  },
  mounted() {
    if (this.$wow && this.$wow.WOW) {
      new this.$wow.WOW().init();
    }
  },
  methods: {
    openContactForm() {
      this.contactFormVisible = true;
    },
    getDayImages(dayIndex) {
      if (this.destId === 0 && SICHUAN_DAY_IMGS[dayIndex]) {
        return SICHUAN_DAY_IMGS[dayIndex];
      }
      if (this.destId === 1 && SCANDCQ_DAY_IMGS[dayIndex]) {
        return SCANDCQ_DAY_IMGS[dayIndex];
      }
      if (this.destId === 2 && BEIJING_DAY_IMGS[dayIndex]) {
        return BEIJING_DAY_IMGS[dayIndex];
      }
      if (this.destId === 3 && XIAN_DAY_IMGS[dayIndex]) {
        return XIAN_DAY_IMGS[dayIndex];
      }
      return [];
    },
  },
};
</script>

<style lang="scss" scoped>
.destination-detail {
  background-color: $box-bg;
}

.box1 {
  background-color: $box-bg;

  .hero {
    position: relative;
    width: 100%;
    height: 420px;
    overflow: hidden;
  }

  .hero-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .hero-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: #fff;
    background: rgba(0, 0, 0, 0.35);
  }

  .hero-tag {
    font-size: 18px;
    letter-spacing: 2px;
    margin-bottom: 12px;
    opacity: 0.95;
  }

  .hero-title {
    margin: 0;
    font-size: 56px;
    font-weight: 700;
    letter-spacing: 4px;
    text-shadow: 0 4px 24px rgba(0, 0, 0, 0.45);
  }

  .detail-inner {
    width: 1200px;
    max-width: calc(100% - 80px);
    margin: 0 auto;
    padding: 48px 0 40px;
    display: flex;
    gap: 40px;
    align-items: flex-start;
  }

  .detail-main {
    flex: 1;
    min-width: 0;
  }

  .highlights-list {
    margin: 20px 0 0;
    padding: 0;
    list-style: none;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 14px 40px;
  }

  .highlights-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 15px;
    line-height: 1.6;
    color: #444;
  }

  .highlights-icon {
    flex-shrink: 0;
    color: #f18000;
    font-weight: bold;
    margin-top: 2px;
  }

  .tour-title {
    margin: 0 0 12px;
    font-size: 32px;
    font-weight: 700;
    color: #1a1a1a;
  }

  .tour-route {
    margin: 0 0 16px;
    font-size: 16px;
    font-weight: 600;
    color: #333;
  }

  .tour-desc {
    margin: 0;
    font-size: 15px;
    line-height: 1.8;
    color: #666;
    text-align: justify;
  }

  .booking-box {
    flex: 0 0 320px;
    padding: 28px 24px;
    background-color: $box-bg;
    border: 1px solid rgba(253, 196, 75, 0.35);
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  }

  .booking-title {
    margin: 0 0 8px;
    font-size: 20px;
    font-weight: 700;
    color: #1a1a1a;
  }

  .booking-contact {
    margin: 0 0 20px;
    font-size: 13px;
    line-height: 1.5;
    color: #666;
  }

  .booking-meta {
    margin: 0 0 24px;
    padding: 0;
  }

  .meta-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px dashed #e0e8f0;
    font-size: 14px;

    dt {
      margin: 0;
      color: #888;
      font-weight: 500;
    }

    dd {
      margin: 0;
      color: #1a1a1a;
      font-weight: 600;
      text-align: right;
    }
  }
}

.buy-btn {
  display: block;
  width: 100%;
  padding: 14px 20px;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(145deg, #ffc04d 0%, #ff8c00 48%, #f07800 100%);
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: transform 0.25s, box-shadow 0.25s;
  box-shadow: 0 8px 20px rgba(255, 140, 0, 0.35);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(255, 140, 0, 0.4);
  }

  &--inline {
    width: auto;
    min-width: 200px;
    margin: 0 25rem 0;
  }
}

.box3,
.box4,
.box5 {
  background-color: $box-bg;
  padding: 0 0 80px;
}

.box3 {
  padding-top: 48px;
}

.box-fee {
  background-color: $box-bg;
  padding: 0 0 80px;

  .fee-block {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .fee-label {
    margin: 0 0 10px;
    font-size: 18px;
    font-weight: 700;
    color: #1a1a1a;
  }

  .fee-text {
    margin: 0;
    font-size: 15px;
    line-height: 1.8;
    color: #666;
    text-align: justify;
  }
}

.section-inner {
  width: 1200px;
  max-width: calc(100% - 80px);
  margin: 0 auto;
}

.section-title {
  margin: 0 0 28px;
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;

  &--center {
    text-align: center;
  }
}

.itinerary-day {
  padding-bottom: 48px;
  border-bottom: 1px solid #e8eef5;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
}

.day-title {
  margin: 0 0 16px;
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 8px;
}

.day-icon {
  color: #f18000;
  font-size: 20px;
}

.day-meta {
  margin: 0 0 16px;
  padding: 0;
  list-style: none;
  font-size: 14px;
  color: #666;
  line-height: 1.8;

  li::before {
    content: "• ";
    color: #fdc44b;
  }
}

.day-para {
  margin: 0 0 12px;
  font-size: 15px;
  line-height: 1.8;
  color: #666;
  text-align: justify;
}

.day-gallery {
  display: flex;
  gap: 16px;
  margin: 20px 0 24px;
  flex-wrap: wrap;

  img {
    width: 322px;
    max-width: 100%;
    height: 200px;
    object-fit: cover;
    border-radius: 10px;
    display: block;
    flex-shrink: 0;
  }

  &--double img {
    width: 485px;
  }
}

.photo-tabs {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-bottom: 28px;
}

.photo-tab {
  padding: 8px 18px;
  font-size: 13px;
  color: #666;
  background: #fff;
  border: 1px solid #e0e8f0;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s;

  &.active,
  &:hover {
    color: #1a1a1a;
    border-color: #fdc44b;
    background: rgba(253, 196, 75, 0.15);
  }
}

.photo-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.photo-cell {
  border-radius: 12px;
  overflow: hidden;

  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
    display: block;
  }
}

.review-section-head {
  text-align: center;
  margin-bottom: 12px;
}

.review-section-label {
  font-size: 14px;
  font-weight: 500;
  color: #fdc44b;
  letter-spacing: 1px;
  margin-bottom: 8px;
}

.review-section-title {
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
}

.review-intro {
  text-align: center;
  max-width: 800px;
  margin: 0 auto 32px;
  font-size: 15px;
  line-height: 1.7;
  color: #666;
}

.review-columns {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.review-column {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.review-scroll {
  max-height: 320px;
  overflow-y: auto;
  margin-bottom: 16px;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.review-block {
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
}

.review-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #999;
  margin-bottom: 8px;
}

.review-stars {
  margin-bottom: 8px;
  color: #fdc44b;

}

.review-block-title {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
}

.review-content {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #666;
}

.review-images {
  margin-top: 12px;

  img {
    width: 200px;
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    object-fit: cover;
  }
}

@media (max-width: 992px) {
  .box1 .detail-inner {
    flex-direction: column;
  }

  .box1 .booking-box {
    flex: none;
    width: 100%;
  }

  .detail-main .highlights-list {
    grid-template-columns: 1fr;
  }

  .day-gallery img {
    width: 100%;
    max-width: 322px;
    height: 200px;
  }

  .day-gallery--double img {
    max-width: 485px;
  }

  .photo-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .review-columns {
    grid-template-columns: 1fr;
  }
}
</style>
